import type { ServicePreview } from '@/components/services/wizard/preview-dock';
import { SALE_MODE, saleModeOf } from '@/components/services/wizard/sale-mode';
import type { ServiceFormData } from '@/lib/validators/service';
import { PRICE_DISPLAY_MODE } from '@/types/price-display-mode';
import { QUOTA_PERIOD } from '@/types/quota-period';
import { RECURRENCE_INTERVAL } from '@/types/recurrence-interval';
import { RECURRING_BOOKING_MODE } from '@/types/recurring-booking-mode';
import { formatDuration } from '@/utils/format-appointment';
import { parseCurrency } from '@/utils/parse-currency';

const brl = (value: number) =>
  value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: value % 1 === 0 ? 0 : 2,
  });

/**
 * O formulário lido como o cliente leria: preço, promessa e chamada.
 *
 * O prestador escreve em campos, mas vende um cartão numa página pública — a
 * prévia é a tradução entre os dois, e é ela que fica na doca.
 */
export function buildServicePreview(values: ServiceFormData): {
  summary: string;
  preview: ServicePreview;
} {
  const subscription = saleModeOf(values) === SALE_MODE.SUBSCRIPTION;

  // Assinatura é sempre mensal; só o avulso que aceita repetição escolhe
  // cadência de cobrança.
  const weekly =
    !subscription &&
    values.recurringBillingInterval === RECURRENCE_INTERVAL.WEEKLY;
  const unitLabel = subscription ? '/mês' : '/sessão';

  const price = parseCurrency(values.defaultPrice);
  const hidden = !price || values.priceDisplayMode === PRICE_DISPLAY_MODE.HIDDEN;
  const priceLabel = hidden
    ? 'Sob consulta'
    : values.priceDisplayMode === PRICE_DISPLAY_MODE.FROM
      ? `a partir de ${brl(price)}`
      : brl(price);

  const durationLabel = values.durationMinutes
    ? formatDuration(values.durationMinutes)
    : '';
  const quotaCount = values.subscriptionQuota ?? 1;
  const quotaWord =
    values.subscriptionQuotaPeriod === QUOTA_PERIOD.WEEK ? 'semana' : 'mês';

  const bullets: string[] = [];
  if (subscription) {
    bullets.push(
      `${quotaCount} ${quotaCount > 1 ? 'sessões' : 'sessão'} por ${quotaWord}, marcadas quando quiser`,
    );
    if (durationLabel) bullets.push(`Cada sessão de ${durationLabel}`);
    bullets.push('Pode deixar sempre no mesmo dia e horário, se preferir');
    bullets.push(`O saldo renova a cada ${quotaWord} e não acumula`);
    bullets.push('Cobrança mensal automática, sem precisar lembrar');
  } else {
    bullets.push(durationLabel ? `Uma sessão de ${durationLabel}` : 'Uma sessão por vez');
    bullets.push('Paga no ato de marcar');
    if (values.recurringBookingMode === RECURRING_BOOKING_MODE.AUTO) {
      bullets.push('Pode repetir as sessões na sua grade, se quiser');
    } else {
      bullets.push('Sem contrato e sem renovação');
    }
  }
  if ((values.slotCapacity ?? 1) > 1) {
    bullets.push(`Turma de até ${values.slotCapacity} pessoas`);
  }

  const cadence = subscription ? `${quotaCount}x por ${quotaWord}` : 'avulso';

  const summary = [
    hidden ? 'Sob consulta' : `${priceLabel}${unitLabel}`,
    cadence,
    durationLabel || 'sem duração',
  ].join(' · ');

  return {
    summary,
    preview: {
      name: values.name?.trim() || 'Serviço sem nome',
      description: values.description?.trim() ?? '',
      priceLabel,
      unitLabel: hidden ? '' : unitLabel,
      bullets,
      ctaLabel: subscription ? 'Assinar' : 'Marcar horário',
      monthlyPerSubscriber:
        subscription && price ? (weekly ? price * 4 : price) : undefined,
    },
  };
}
